import { useEffect, useState } from "react";
import type { Authorization, Invoice, SubscriptionOverview, TermsTexts } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { saveFile } from "../../lib/files";
import { formatEuro } from "../../lib/money";
import { formatDate } from "../../lib/time";
import { LegalDialog } from "../terms/LegalDialog";
import { LEGAL_DOCUMENTS, TermsGate, type LegalDocument } from "../terms/TermsGate";
import { InvoiceDialog } from "./InvoiceDialog";
import {
  acceptanceText,
  dayDate,
  daysText,
  monthName,
  priceText,
  showsDays,
  statusText,
  type Rich,
} from "./subscription";

interface Props {
  /** The owner's Beheer code. */
  authorization: Authorization;
}

function RichText({ parts }: { parts: Rich }) {
  return (
    <>
      {parts.map((part, index) => (part.bold ? <b key={index}>{part.text}</b> : <span key={index}>{part.text}</span>))}
    </>
  );
}

/**
 * Beheer › Abonnement & facturen, as in the prototype (only the owner): the Tafo subscription with its
 * status, the invoices and credit notes (to view and download as PDF) and the contract with the proof
 * of acceptance. Paying is done in the payment window of the till (LicencePayment.tsx), at Mollie only.
 */
export function SubscriptionScreen({ authorization }: Props) {
  const { api, report, toast, subscriptionRevision } = useTill();
  const [overview, setOverview] = useState<SubscriptionOverview | null>(null);
  const [shown, setShown] = useState<Invoice | null>(null);
  const [texts, setTexts] = useState<TermsTexts | null>(null);
  const [legal, setLegal] = useState<LegalDocument | null>(null);
  const [gate, setGate] = useState(false);
  const text = t.subscription;

  useEffect(() => {
    let current = true;
    api
      .request<SubscriptionOverview>("GET", "/device/subscription", { staffToken: authorization.token, retry: true })
      .then((answer) => {
        if (current) setOverview(answer);
      })
      .catch(report);
    return () => {
      current = false;
    };
  }, [api, authorization.token, report, subscriptionRevision]);

  const download = async (invoice: Invoice) => {
    try {
      const file = await api.download(`/device/invoices/${invoice.id}/pdf`, { staffToken: authorization.token });
      saveFile(file);
      toast(text.invoices.downloaded(file.filename));
    } catch (error) {
      report(error);
    }
  };

  const openLegal = async (document: LegalDocument) => {
    try {
      setTexts(texts ?? (await api.request<TermsTexts>("GET", "/device/terms", { retry: true })));
      setLegal(document);
    } catch (error) {
      report(error);
    }
  };

  if (overview === null) {
    return (
      <div className="panel admin-section subscription-admin">
        <h2>{text.title}</h2>
      </div>
    );
  }
  const { licence, invoices, contract } = overview;
  const plan = text.plan;
  const bills = invoices.filter((invoice) => invoice.kind === "invoice");
  const credits = invoices.filter((invoice) => invoice.kind === "credit_note");

  return (
    <div className="panel admin-section subscription-admin">
      <h2>{text.title}</h2>

      <div className="zbox">
        <h3>{plan.title}</h3>
        <div className="kvs">
          <div>
            <span>{plan.package}</span>
            <b>{plan.packageName}</b>
          </div>
          <div>
            <span>{plan.price}</span>
            <b>{priceText(licence)}</b>
          </div>
          <div>
            <span>{plan.paidUntil}</span>
            <b>{dayDate(licence.paidUntil)}</b>
          </div>
          <div>
            <span>{plan.status}</span>
            <b>{statusText(licence)}</b>
          </div>
        </div>
        {showsDays(licence) ? (
          <p className={`lic-days${licence.state === "expired" ? " blocked" : ""}`}>
            ⏳ <RichText parts={daysText(licence)} />
          </p>
        ) : (
          <p className="note sub-note">{plan.note(Number(licence.nextPeriod.slice(8)))}</p>
        )}
      </div>

      <div className="zbox">
        <h3>{text.invoices.title}</h3>
        {invoices.length > 0 ? (
          <div className="tablewrap">
            <table className="stafftbl invtbl stack">
              <thead>
                <tr>
                  <th>{text.invoices.columns.document}</th>
                  <th>{text.invoices.columns.date}</th>
                  <th>{text.invoices.columns.period}</th>
                  <th>{text.invoices.columns.paidWith}</th>
                  <th className="r">{text.invoices.columns.amount}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {bills.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="num grow">
                      <b>{invoice.number}</b>
                    </td>
                    <td className="num later" data-label={text.invoices.columns.date}>
                      {formatDate(invoice.issuedAt)}
                    </td>
                    <td className="later" data-label={text.invoices.columns.period}>
                      {monthName(invoice.period)}
                    </td>
                    <td className="later" data-label={text.invoices.columns.paidWith}>
                      {invoice.paidWith}
                    </td>
                    <td className="r num">{formatEuro(invoice.totalCents)}</td>
                    <td className="r last">
                      <button
                        type="button"
                        className="btn"
                        onClick={() => {
                          setShown(invoice);
                        }}
                      >
                        {text.invoices.view}
                      </button>{" "}
                      <button type="button" className="btn" onClick={() => void download(invoice)}>
                        {text.invoices.pdf}
                      </button>
                    </td>
                  </tr>
                ))}
                {credits.map((creditNote) => (
                  <tr key={creditNote.id}>
                    <td className="num grow">
                      <b>{creditNote.number}</b> <span className="pill bad">{text.invoices.creditNote}</span>
                    </td>
                    <td className="num later" data-label={text.invoices.columns.date}>
                      {formatDate(creditNote.issuedAt)}
                    </td>
                    <td className="later" data-label={text.invoices.columns.period}>
                      {monthName(creditNote.period)}
                    </td>
                    <td className="later">{creditNote.reason}</td>
                    <td className="r num">− {formatEuro(creditNote.totalCents)}</td>
                    <td className="r last">
                      <button
                        type="button"
                        className="btn"
                        onClick={() => {
                          setShown(creditNote);
                        }}
                      >
                        {text.invoices.view}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="note">{text.invoices.none}</p>
        )}
      </div>

      <div className="zbox">
        <h3>{text.contract.title}</h3>
        <p className="contract-line">
          {contract.acceptance ? acceptanceText(contract.acceptance) : text.contract.notAccepted}
        </p>
        <p className="note legal-links">
          {LEGAL_DOCUMENTS.map((document, index) => (
            <span key={document}>
              {index > 0 && " · "}
              <button type="button" className="linkbtn" onClick={() => void openLegal(document)}>
                {t.terms.documents[document]}
              </button>
            </span>
          ))}
        </p>
        {contract.upcoming !== null && (
          <div className="lic-days akknew">
            {text.contract.upcoming(
              contract.upcoming.version,
              formatDate(contract.upcoming.effectiveFrom),
              contract.upcoming.summary ?? "",
              contract.upcoming.requiresReacceptance,
            )}{" "}
            <button
              type="button"
              className="btn"
              onClick={() => {
                setGate(true);
              }}
            >
              {text.contract.readAndAccept}
            </button>
          </div>
        )}
      </div>

      {shown !== null && (
        <InvoiceDialog
          invoice={shown}
          onDownload={() => void download(shown)}
          onClose={() => {
            setShown(null);
          }}
        />
      )}
      {legal !== null && texts !== null && (
        <LegalDialog
          texts={texts}
          document={legal}
          onClose={() => {
            setLegal(null);
          }}
        />
      )}
      {gate && (
        <TermsGate
          api={api}
          mode="new"
          onAccepted={() => {
            setGate(false);
          }}
          onClose={() => {
            setGate(false);
          }}
        />
      )}
    </div>
  );
}
